import { UIEvent, useCallback, useRef } from 'react';
import { Location, useLocation, useNavigate } from 'react-router';

import { Path } from '#/service/routes';
import { techSearchParams } from '#/service/utils';

const debugLog = (_debugInfo?: string, ..._params: any[]) => {
  // uncomment the console.log here to get debug info about all the redirections.
  // console.log('useNavigation:', _debugInfo, ..._params);
};

export interface UseNavigationResult {
  /**
   * Navigates to the previous page in the history.
   * @param [fallbackPathOrEvent] {Path | UIEvent} Where to navigate to if we can't go back.
   * @param [goBackLocation] {Location} If set, it will navigate to that exact location.
   * The UIEvent type is only allowed to make the function directly usable as an event handler.
   * If the history key is 'default' it navigates to the home page unless there is a fallback path provided.
   */
  goBack: (fallbackPath?: Path | UIEvent, goBackLocation?: Location) => void;
  goHome: () => void;
  /**
   * Method used for navigating between route states
   * @param path {string} The path to navigate to.
   * @param state {Object} The state to pass to the new route.
   * Please do not put any sensitive data in here.
   * Also do not put too much data here as it may affect performance,
   * because state is often serialized and deserialized
   */
  go: (path: string, state?: Object) => void;
  reloadPage: () => void;
  redirect: (redirectUrl: string) => void;
}

/**
 * Common navigation actions.
 * @param debugInfo {string} Info about what component is using navigation and why. For debugging.
 * @returns {UseNavigationResult} Common navigation functions.
 */
export const useNavigation = (debugInfo: string): UseNavigationResult => {
  const navigate = useNavigate();
  const location = useLocation();

  // to prevent unnecessary re-renders, we don't want our functions to depend on the location. using refs instead.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;

  const goHome = useCallback(() => {
    debugLog(debugInfo, 'goHome');

    // if already at home, just scroll to top
    if (locationRef.current.pathname === '/') {
      window.scrollTo(0, 0);
    } else {
      navigateRef.current({
        pathname: `/`,
        search: techSearchParams(locationRef.current.search),
      });
    }
  }, [debugInfo]);

  const goBack = useCallback(
    (fallbackPath?: Path | UIEvent, goBackLocation?: Location) => {
      // The documentation for the location.key mention that it will always be 'default' on intial load.
      // Therefore we should be able to use it to determine if the user already navigated around the app or not.
      // https://github.com/remix-run/history/blob/main/docs/api-reference.md#locationkey:~:text=location.key,device%20storage%20API.
      if (locationRef.current.key === 'default') {
        // if the page was deep linked / initial loaded, go back to the home page.
        debugLog(debugInfo, 'goBack→goHome (default key)');
        goHome();

        return;
      }

      // we have to make sure that fallbackPath is a string, because when goBack is used directly as an event
      // handler (like <button onClick={goBack} />), the first parameter will be a UIEvent object.
      if (typeof fallbackPath === 'string') {
        debugLog(debugInfo, 'goBack→fallbackPath', fallbackPath);
        navigateRef.current(
          {
            pathname: fallbackPath,
            search: techSearchParams(locationRef.current.search),
          },
          { replace: true }
        );

        return;
      }

      if (goBackLocation) {
        // if goBackLocation is set, we want to go back to that exact previous location kept in useNavigationHistory.
        debugLog(debugInfo, 'goBack→goBackLocation');
        navigateRef.current({
          pathname: goBackLocation?.pathname,
          search: techSearchParams(goBackLocation?.search),
        });

        return;
      }

      // If not default and no fallback, only go back if there are more than 2 entries in the history.
      if (window.history.length > 2) {
        debugLog(debugInfo, 'goBack->navigate(-1)');
        navigateRef.current(-1);

        return;
      }

      debugLog(debugInfo, 'goBack→goHome (history too short)');
      goHome();
    },
    [debugInfo, goHome]
  );

  const go = useCallback(
    (path: string, state?: Object) => {
      debugLog(debugInfo, 'go');
      navigateRef.current(
        {
          pathname: path,
          search: techSearchParams(locationRef.current.search),
        },
        {
          state,
        }
      );
    },
    [debugInfo]
  );

  const reloadPage = useCallback(() => {
    debugLog(debugInfo, 'reloadPage');
    navigateRef.current(0);
  }, [debugInfo]);

  const redirect = useCallback(
    (redirectUrl: string) => {
      debugLog(debugInfo, 'redirect', redirectUrl);
      window.location.href = redirectUrl;
    },
    [debugInfo]
  );

  return {
    goHome,
    goBack,
    go,
    reloadPage,
    redirect,
  };
};
